/**
 * Derived geometry (TD §4 "Rendering is derived"): what the renderer, hit testing and analysis draw and measure.
 * Results are cached per object, keyed by object identity. Immer keeps unchanged objects identical between
 * versions of the doc, so after an edit only the touched areas, roads and rivers are recomputed.
 */
import { isDraft } from 'immer';
import type { AreaId, PlanDoc, Pt, RiverId, Road, RoadId, RoadNode, Vert } from '../../shared/types';
import { polyArea } from './basic';
import { riverPoly, ringVerts, roadNodes, roadPolyFromNodes, smoothRing, type RoadPoly } from './shapes';

/** Everything drawn from the plan's topology. */
export interface Derived {
  /** The zoo boundary, smoothed unless `plot.smooth` is false; null without a boundary. */
  plotRing: Pt[] | null;
  /** Smoothed rings, Chaikin 2 passes, pinned corners kept. */
  areaRings: Record<AreaId, Pt[]>;
  /** Catmull-Rom centre lines (SEG samples per span) with the width at every sample. */
  roadPolys: Record<RoadId, RoadPoly>;
  /** River centre lines. */
  riverPolys: Record<RiverId, Pt[]>;
}

interface RingEntry { verts: Vert[]; smooth: boolean; ring: Pt[] }
interface RoadEntry { nodes: RoadNode[]; closed: boolean; poly: RoadPoly }

const ringCache = new WeakMap<object, RingEntry>();
const roadCache = new WeakMap<Road, RoadEntry>();
const riverCache = new WeakMap<object, Pt[]>();
const docCache = new WeakMap<PlanDoc, Derived>();

const sameRefs = <T>(a: readonly T[], b: readonly T[]) => a.length === b.length && a.every((x, i) => x === b[i]);

/** Ring of a corner list, cached on `owner` (the area or plot object) and checked against the corner objects. */
function cachedRing(doc: PlanDoc, owner: { verts: string[] }, smooth: boolean, cache: boolean): Pt[] {
  const verts = ringVerts(doc, owner.verts);
  if (cache) {
    const hit = ringCache.get(owner);
    if (hit && hit.smooth === smooth && sameRefs(hit.verts, verts)) return hit.ring;
  }
  const ring = smoothRing(verts, smooth);
  if (cache) ringCache.set(owner, { verts, smooth, ring });
  return ring;
}

/**
 * Computes the derived geometry of a plan. Pure; repeated calls on the same (or a structurally shared) doc
 * reuse earlier results. Called on an immer draft it still works but caches nothing.
 */
export function derive(doc: PlanDoc): Derived {
  const cache = !isDraft(doc);
  if (cache) { const hit = docCache.get(doc); if (hit) return hit; }

  const areaRings: Record<AreaId, Pt[]> = {};
  for (const [id, a] of Object.entries(doc.areas)) areaRings[id] = cachedRing(doc, a, true, cache);

  const roadPolys: Record<RoadId, RoadPoly> = {};
  for (const [id, r] of Object.entries(doc.roads)) {
    const nodes = roadNodes(doc, r);
    const hit = cache ? roadCache.get(r) : undefined;
    if (hit && hit.closed === r.closed && sameRefs(hit.nodes, nodes)) { roadPolys[id] = hit.poly; continue; }
    const poly = roadPolyFromNodes(nodes, r.closed);
    if (cache) roadCache.set(r, { nodes, closed: r.closed, poly });
    roadPolys[id] = poly;
  }

  const riverPolys: Record<RiverId, Pt[]> = {};
  for (const [id, rv] of Object.entries(doc.rivers)) {
    let poly = cache ? riverCache.get(rv) : undefined;
    if (!poly) { poly = riverPoly(rv.pts); if (cache) riverCache.set(rv, poly); }
    riverPolys[id] = poly;
  }

  const plotRing = doc.plot ? cachedRing(doc, doc.plot, doc.plot.smooth, cache) : null;
  const out: Derived = { plotRing, areaRings, roadPolys, riverPolys };
  if (cache) docCache.set(doc, out);
  return out;
}

/** Size of an area in m²: the area of its smoothed ring (TD §4), 0 if the area doesn't exist. */
export function areaSize(derived: Derived, areaId: AreaId): number {
  const ring = derived.areaRings[areaId];
  return ring ? polyArea(ring) : 0;
}
